import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis(({ scroll }) => setScrolled(scroll > 40));

  // Backup for the scrolled state: the Lenis callback does not fire for every
  // scroll source (extensions, keyboard, native wheel fallbacks).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toTop = () => {
    setOpen(false);
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 border-b ${
        scrolled
          ? "nav-solid backdrop-blur-md border-ivory/10"
          : "border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-shell px-6 md:px-10">
        <div className="flex items-center justify-between h-[70px]">
          <button
            onClick={toTop}
            className="flex items-center gap-4 group"
            aria-label="G&F Customs — back to top"
          >
            <span className="font-display text-[1.55rem] leading-none tracking-tight text-ivory">
              G&amp;F
            </span>
            <span className="hidden sm:block h-6 w-px bg-ivory/20" />
            <span className="hidden sm:block label-xs text-mist text-left leading-[1.5]">
              Luxury Retail
              <br />
              Environments
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => goTo(l.href)}
                className="nav-link label-xs text-mist hover:text-ivory transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => goTo("#contact")}
              className="label-xs text-ivory border border-ivory/35 px-6 py-3 hover:border-champagne hover:text-champagne transition-colors duration-300"
            >
              Start a project
            </button>
          </div>

          <button
            className="lg:hidden text-ivory p-2 -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-obsidian border-t border-ivory/10 px-6 pb-8 pt-2">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => goTo(l.href)}
              className="block w-full text-left py-4 font-display text-2xl text-ivory border-b border-ivory/10"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => goTo("#contact")}
            className="mt-6 w-full label-xs text-obsidian bg-champagne px-5 py-4"
          >
            Start a project
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
